import { csvCell } from '~/utils/csvCell';

import { campaignChoices, dealFields } from './campaignPicker';
import { type Variant, metricColumns, vendorChange } from './dashboard';
import type { Snapshot } from './data';
import type { adaptPost } from './liveDataAdapter';
import { postIdentity } from './liveDataAdapter';
import {
  type Filters,
  build,
  cumulativeChannelViews,
  pcmMetrics,
  sourceMetricChangeLabel,
  vendorMetrics,
} from './model';

export function postCsvForScope(
  settled: ReturnType<typeof adaptPost>,
  identity: string,
  variant: Variant | undefined,
  only: boolean,
  validationSnapshot = settled.snapshot,
) {
  if (
    !settled.ready ||
    settled.identity !== identity ||
    !settled.snapshot ||
    !settled.filters ||
    settled.filtersIdentity !== JSON.stringify(settled.filters) ||
    (variant && !settled.vendor?.variants.includes(variant))
  ) {
    throw new Error('The report is not ready for this export scope.');
  }
  return (
    '\uFEFF' +
    [
      ['Source as-of', 'Unknown'],
      ['Effective scope', JSON.stringify(settled.scope)],
    ]
      .map((row) => row.map((value) => csvCell(value, 'Unknown')).join(','))
      .join('\r\n') +
    '\r\n' +
    postCsv(settled.snapshot, settled.filters, variant, only, validationSnapshot).slice(1)
  );
}

export function dealCsvForScope(settled: ReturnType<typeof adaptPost>, params: URLSearchParams): string {
  if (
    !settled.ready ||
    settled.identity !== postIdentity(params) ||
    settled.filtersIdentity !== JSON.stringify(settled.filters)
  ) {
    throw new Error('The source catalogue is not ready for this export scope.');
  }
  const columns: ReadonlyArray<keyof Snapshot['campaigns'][number]> = [
    ...dealFields,
    'offer_bk',
    'vendor_bk',
    'start_date',
    'end_date',
    'first_transaction_date',
    'first_sale_date',
    'last_transaction_date',
    'campaign_type',
  ];
  return (
    '\uFEFF' +
    [
      [
        'Source',
        'analytics.sales_with_refunds',
        'Source as-of',
        'Unknown',
        'Effective scope',
        JSON.stringify(settled.scope),
        'Picker scope',
        params.toString(),
      ],
      columns,
      ...campaignChoices(settled.snapshot.campaigns, params).map((row) => columns.map((key) => row[key])),
    ]
      .map((row) => row.map((value) => csvCell(value, 'Unknown')).join(','))
      .join('\r\n')
  );
}

export function postCsv(
  snapshot: Snapshot,
  filters: Filters,
  variant: Variant | undefined,
  only: boolean,
  validationSnapshot = snapshot,
) {
  const model = build(snapshot, filters, true, validationSnapshot);
  const values: Array<Array<unknown>> = [
    ['Report', 'Flash Post Campaign'],
    ['Current', filters.current, filters.from, filters.to],
    ...(!only ? [['Comparison', filters.comparison, filters.cmpFrom, filters.cmpTo]] : []),
    ['Currency', filters.currency],
    ['Refund', filters.refund],
    ['Booking evaluation date (Australia/Sydney)', snapshot.meta.bookingAsOf ?? snapshot.meta.asOf],
    ['Current campaign only', only],
    ['Filters', JSON.stringify({ room: filters.room, packageType: filters.packageType, booking: filters.booking })],
  ];
  const metrics = [...vendorMetrics, ...pcmMetrics].filter(
    (metric, index, list) => list.findIndex((item) => item.id === metric.id) === index,
  );
  for (const [section, groups] of Object.entries({
    rooms: model.rooms,
    roomGroupSize: model.roomGroupSize,
    customers: model.customers,
    stays: model.stays,
  })) {
    values.push(
      [],
      ['PCM section', section],
      ['Group', 'Measure', 'Current', ...(!only ? ['Comparison', 'Change'] : [])],
    );
    groups.forEach((group) =>
      metrics.forEach((metric) =>
        values.push([
          group.name,
          metric.label,
          group.current[metric.id],
          ...(!only
            ? [
                group.comparison[metric.id],
                sourceMetricChangeLabel(group.current[metric.id], group.comparison[metric.id], metric.id),
              ]
            : []),
        ]),
      ),
    );
  }
  values.push(
    [],
    ['Traffic channel', 'Current views', ...(!only ? ['Comparison views'] : [])],
    ...model.channels.map((row) => [row.name, row.current, ...(!only ? [row.comparison] : [])]),
    [],
    [
      'Campaign day',
      'Current cumulative room nights',
      'Current cumulative views',
      ...(!only ? ['Comparison cumulative room nights', 'Comparison cumulative views'] : []),
    ],
    ...model.curve.map((row) => [row.day, row.current, row.views, ...(!only ? [row.comparison, row.cmpViews] : [])]),
  );
  values.push([], ['Traffic channel series', 'Campaign day', 'Cumulative views']);
  cumulativeChannelViews(model, filters, only).forEach((series) =>
    series.data.forEach((value, index) => values.push([series.name, model.curve[index].day, value])),
  );
  if (variant) {
    if (!only) {
      for (const metric of metricColumns) {
        const value = vendorChange(
          metric.key,
          metric.key === 'revenue' ? variant.windowCurrentRevenue : variant.current[metric.key],
          variant.comparison[metric.key],
        );
        values.push([
          'Vendor change',
          metric.label,
          value,
          metric.percent ? 'Fractional point difference' : 'Relative change',
        ]);
      }
      if (variant.hasInclusions) {
        values.push(['Vendor change', 'Inclusion upgrades', variant.inclusionDelta, 'Fractional point difference']);
      }
    }

    values.push(
      [],
      ['Selected vendor period', variant.period],
      ['Current through', variant.to],
      ...(!only ? [['Comparison through', variant.cmpTo]] : []),
    );
    const append = (path: string, value: unknown) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => append(path + '[' + index + ']', item));
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([key, item]) => {
          if (only && ['comparison', 'cmpTo', 'inclusionDelta'].includes(key)) {
            return;
          }
          append(path + '.' + key, item);
        });
      } else {
        values.push([path, value]);
      }
    };
    append('Vendor', variant);
  }
  const contents = '\uFEFF' + values.map((r) => r.map((x) => csvCell(x, 'Unknown')).join(',')).join('\r\n');
  return contents;
}
